import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowRight, Phone, MapPin, Check, Euro, 
  Scissors, TreeDeciduous, Leaf, Droplets, Axe, Palette,
  Clock, Shield, Star
} from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceIcons = {
  'tonte': Scissors,
  'haies': TreeDeciduous,
  'debroussaillage': Leaf,
  'arrosage': Droplets,
  'elagage': Axe,
  'amenagement': Palette
};

const CityPage = () => {
  const { citySlug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Only process if it looks like a city page
    if (!citySlug || !citySlug.startsWith('jardinier-')) {
      setError(true);
      setLoading(false);
      return;
    }

    const fetchPage = async () => {
      try {
        const res = await axios.get(`${API}/city-pages/${citySlug}`);
        setPageData(res.data);
        // Update page title
        document.title = res.data.meta_title;
        // Track page view
        await axios.post(`${API}/city-pages/${citySlug}/view`).catch(() => {});
      } catch (err) {
        console.error('Error fetching city page:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [citySlug]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (error || !pageData) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-stone-900 mb-4">Page non trouvée</h1>
            <Link to="/">
              <Button className="btn-primary">Retour à l'accueil</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const services = [
    { icon: Scissors, title: 'Tonte de pelouse', desc: 'Entretien régulier de votre gazon' },
    { icon: TreeDeciduous, title: 'Taille de haies', desc: 'Mise en forme de vos haies' },
    { icon: Leaf, title: 'Débroussaillage', desc: 'Nettoyage des terrains' },
    { icon: Axe, title: 'Élagage', desc: 'Taille de vos arbres' },
    { icon: Droplets, title: 'Arrosage auto', desc: 'Installation et maintenance' },
    { icon: Palette, title: 'Aménagement', desc: 'Création de jardins' }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 py-20 pt-32 lg:pt-40 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-96 h-96 bg-emerald-400/20 rounded-full blur-[100px]"></div>
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-yellow-400/10 rounded-full blur-[80px]"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
              <MapPin className="w-4 h-4" />
              {pageData.city_name}
            </div>
            
            <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
              {pageData.h1_title}
            </h1>
            
            <div 
              className="text-emerald-100 text-lg md:text-xl mb-8 prose prose-invert prose-p:text-emerald-100"
              dangerouslySetInnerHTML={{ __html: pageData.intro_text }}
            />
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/entretien">
                <Button className="bg-white text-emerald-700 hover:bg-emerald-50 rounded-full px-8 py-4 font-semibold shadow-lg" data-testid="city-cta-entretien">
                  Réserver un entretien
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:0465011022">
                <Button variant="outline" className="bg-transparent text-white border-white/30 hover:bg-white/10 rounded-full px-8 py-4">
                  <Phone className="w-5 h-5 mr-2" />
                  04 65 01 10 22
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Credit d'impôts Banner */}
      <section className="bg-gradient-to-r from-amber-500 to-orange-500 py-5">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-white">
              <Euro className="w-8 h-8" />
              <div>
                <p className="font-bold text-lg">Crédit d'impôts 50%</p>
                <p className="text-amber-100 text-sm">Avance immédiate - Vous ne payez que la moitié</p>
              </div>
            </div>
            <Link to="/credit-impot">
              <Button className="bg-white text-amber-600 hover:bg-amber-50 rounded-full px-6 font-semibold">
                En savoir plus
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white" data-testid="city-services">
        <div className="container-custom">
          <div 
            className="prose prose-lg max-w-none mb-12 prose-h2:font-heading prose-h2:text-3xl prose-h2:text-stone-900"
            dangerouslySetInnerHTML={{ __html: pageData.services_text }}
          />
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group bg-stone-50 rounded-2xl p-6 hover:bg-emerald-50 hover:shadow-lg transition-all"
              >
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-emerald-500 transition-colors">
                  <service.icon className="w-7 h-7 text-emerald-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-stone-900 mb-2">
                  {service.title}
                </h3>
                <p className="text-stone-600 text-sm">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-stone-50" data-testid="city-why-us">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div 
                className="prose prose-lg max-w-none prose-h2:font-heading prose-h2:text-3xl prose-h2:text-stone-900 prose-p:text-stone-600"
                dangerouslySetInnerHTML={{ __html: pageData.local_text }}
              />
              
              <div className="grid grid-cols-2 gap-6 mt-8">
                <div className="text-center p-4 bg-white rounded-2xl shadow-sm">
                  <p className="font-heading font-bold text-3xl text-emerald-600 mb-1">10+</p>
                  <p className="text-stone-600 text-sm">Années d'expérience</p>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-sm">
                  <p className="font-heading font-bold text-3xl text-emerald-600 mb-1">500+</p>
                  <p className="text-stone-600 text-sm">Jardins entretenus</p>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-sm">
                  <p className="font-heading font-bold text-3xl text-emerald-600 mb-1">98%</p>
                  <p className="text-stone-600 text-sm">Clients satisfaits</p>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-sm">
                  <p className="font-heading font-bold text-3xl text-emerald-600 mb-1">24h</p>
                  <p className="text-stone-600 text-sm">Délai intervention</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/1453499/pexels-photo-1453499.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt={`Jardinier à ${pageData.city_name}`}
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="flex">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <div>
                    <p className="font-semibold text-stone-900">4.9/5</p>
                    <p className="text-stone-500 text-xs">127 avis</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guarantees */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Check className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1">Devis gratuit</h3>
                <p className="text-stone-600 text-sm">Estimation sans engagement sous 24h</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1">Satisfaction garantie</h3>
                <p className="text-stone-600 text-sm">Reprise gratuite si non satisfait</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1">Intervention rapide</h3>
                <p className="text-stone-600 text-sm">Disponibilité sous 24-48h</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-600" data-testid="city-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
            {pageData.cta_text}
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/entretien">
              <Button className="bg-white text-emerald-600 hover:bg-stone-100 rounded-full px-10 py-5 font-bold text-lg shadow-xl">
                Réserver un entretien
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="bg-transparent text-white border-white/50 hover:bg-white/10 rounded-full px-10 py-5 font-semibold text-lg">
                Tous nos services
              </Button>
            </Link>
          </div>
          <div className="mt-8">
            <a 
              href="tel:0465011022" 
              className="inline-flex items-center gap-2 text-emerald-100 hover:text-white transition-colors"
            >
              <Phone className="w-5 h-5" />
              <span className="font-semibold">04 65 01 10 22</span>
            </a>
          </div>
        </div>
      </section>

      {/* Other Cities */}
      <section className="py-16 bg-stone-50">
        <div className="container-custom">
          <h2 className="font-heading font-bold text-2xl text-stone-900 mb-6 text-center">
            Nous intervenons également dans ces villes
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {['Istres', 'Fos-sur-Mer', 'Miramas', 'Martigues', 'Port-de-Bouc', 'Salon-de-Provence', 
              'Vitrolles', 'Marignane', 'Berre-l\'Étang', 'Saint-Chamas', 'Grans', 'Cornillon-Confoux',
              'Saint-Mitre-les-Remparts', 'Châteauneuf-les-Martigues', 'Carry-le-Rouet', 'Sausset-les-Pins',
              'Port-Saint-Louis-du-Rhône', 'Arles', 'Les Pennes-Mirabeau', 'Coudoux']
              .filter(city => city !== pageData.city_name)
              .map((city) => (
              <Link 
                key={city}
                to={`/jardinier-${city.toLowerCase().replace(/['\s]/g, '-').replace(/[éè]/g, 'e')}`}
                className="px-4 py-2 bg-white rounded-full border border-stone-200 text-stone-700 hover:border-emerald-500 hover:text-emerald-600 transition-colors text-sm font-medium"
              >
                {city}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CityPage;
